/* ⚠️ GENERATED from app/services/worldMap.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   🌍 worldMap.ts — the rules of the world map. One module, three readers: the
   phone and the browser draw from it, and the server (mapRoutes.js, through
   its CommonJS twin) enforces it.

   WHERE SOMEBODY IS — 🟥 A CITY THEY CHOSE, NEVER WHERE THEIR PHONE IS
     The map never reads GPS. Somebody's place is the current city on their
     profile, or another city they pick on the map ("Dearborn" on the profile,
     "Ann Arbor" on the map is fine) — and it stays put while they travel.
     No city anywhere → they are asked to add one before they appear. The
     SERVER turns the city into a position (a geocoder's city centre), so no
     client can store more than a city.
          people   precision 6  (the city centre's cell)
          pages    precision 7  ≈ 150 m          (a shop is meant to be found)
     The world is drawn from COUNTS, never from people:
          mapCells/{gh3}   ≈ 156 km   people + pages, for the far view
          mapCells5/{gh5}  ≈ 4.9 km   people, for the near view's anonymous dots
     Only people who chose Public are listed one by one (mapPublic/{uid}), and
     only friends who allow it come back with a name (the friends route).

   WHO SEES WHAT — users/{uid}.privacySettings.mapVisibility
     'everyone'  a dot of its own; anyone may tap it for the name and picture
     'friends'   friends see the name on the map; everyone else sees a dot
     'anon'      a dot, counted, never named — THE DEFAULT (absent = 'anon')
     'nobody'    not on the map at all, not even counted

   ONLINE — users/{uid}.privacySettings.mapOnline
     Everyone placed is on the map, online or not; the online ones are GREEN.
     'everyone'  green to all, and counted in "online now"
     'friends'   green to friends only — grey (and uncounted) to everyone else
     'nobody'    always grey: "appear offline"
     Unset, it follows the chat's own Online / Last seen setting.

   THE SKY behind the globe is the viewer's own: the sun's height where they
   are (computed here, no service needed) and the weather (the server asks a
   weather service once per ~150 km area and shares the answer).
   ═══════════════════════════════════════════════════════════════════════════ */

export const MAP_VISIBILITIES = ['everyone', 'friends', 'anon', 'nobody'];

/** Geohash precisions — see the header. */
export const MAP_PERSON_PRECISION = 6;
export const MAP_PAGE_PRECISION = 7;
export const MAP_CELL_PRECISION = 3;
export const MAP_FINE_PRECISION = 5;
export const MAP_NEAR_PRECISION = 4;

/** A place not looked at for this long is re-checked (does the account still exist?) — never removed for being quiet. */
export const MAP_RECHECK_MS = 30 * 86400000;
/** The app checks that its owner is on the map at most this often without being asked. */
export const MAP_QUIET_SYNC_MS = 12 * 3600000;

/* ── visibility ──────────────────────────────────────────────────────────── */

/** The setting as it stands — anything unrecognised is the default, a nameless dot. */
export function mapVisibilityOf(user) {
  const v = user && user.privacySettings && user.privacySettings.mapVisibility;
  return v === 'everyone' || v === 'friends' || v === 'nobody' ? v : 'anon';
}

/** Is this person on the map at all (as a counted dot)? */
export function onMap(vis) {
  return vis !== 'nobody';
}

/**
 * May `viewer` see this person's NAME and PICTURE on the map?
 * 🟥 A block in either direction hides them whatever the setting says.
 * 🟢 I always see myself.
 */
export function canSeeIdentity(vis, o) {
  if (o && o.isSelf) return vis !== 'nobody';
  if (!o || o.blocked) return false;
  if (vis === 'everyone') return true;
  if (vis === 'friends') return !!o.isFriend;
  return false;
}

/* ── online ──────────────────────────────────────────────────────────────── */

export const MAP_ONLINE_CHOICES = ['everyone', 'friends', 'nobody'];

/**
 * Who may see that somebody is online, on the map. Their own map choice if they
 * made one; otherwise the chat's Online / Last seen setting decides — somebody
 * who hides their last seen from everyone is not lit up green on a world map.
 */
export function mapOnlineOf(user) {
  const ps = user && user.privacySettings || {};
  const v = ps.mapOnline;
  if (v === 'everyone' || v === 'friends' || v === 'nobody') return v;
  if (ps.online === 'everyone') return 'everyone';
  if (ps.lastSeen === 'nobody') return 'nobody';
  if (ps.lastSeen === 'my_contacts' || ps.lastSeen === 'except') return 'friends';
  return 'everyone';
}

/** May `viewer` see this person as online? 🟥 A block hides it; 🟢 I always see my own. */
export function canSeeOnline(on, o) {
  if (o && o.isSelf) return true;
  if (!o || o.blocked) return false;
  if (on === 'everyone') return true;
  if (on === 'friends') return !!o.isFriend;
  return false;
}

/* ── appearing as a page ─────────────────────────────────────────────────── */

/**
 * The page somebody has chosen to appear as on the map, or null. While set,
 * their own face and name are never drawn for anyone else — their PAGE is drawn
 * instead, at the page's address, with its logo (and lit when they are online,
 * as their online setting allows). They still count in their city, nameless.
 * 🟥 The server checks the page is theirs before it ever draws it.
 */
export function mapAsPageOf(user) {
  const v = user && user.privacySettings && user.privacySettings.mapAsPage;
  return typeof v === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(v) ? v : null;
}

/* ── the place: a city ───────────────────────────────────────────────────── */

const fold = x => String(x || '').toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();

/** One key per city, however it was typed: "Ann Arbor|michigan|US". */
export function placeKey(p) {
  if (!p || !p.name) return '';
  return `${fold(p.name)}|${fold(p.region)}|${String(p.cc || '').toUpperCase()}`;
}

/** "Ann Arbor, Michigan" — the region only when it adds something. */
export function placeLabel(p) {
  if (!p || !p.name) return '';
  const bits = [p.name];
  if (p.region && fold(p.region) !== fold(p.name)) bits.push(p.region);else if (p.country) bits.push(p.country);
  return bits.join(', ');
}
/**
 * Of a geocoder's answers for a city name, the one that IS the city asked for:
 * same country and region first, then same country, then the biggest place of
 * that name. A name alone ("Paris") means the largest Paris.
 */
export function pickGeocode(results, want) {
  const ok = r => r && Number.isFinite(Number(r.latitude)) && Number.isFinite(Number(r.longitude)) && !!r.name;
  const list = (results || []).filter(ok);
  if (!list.length) return null;
  const cc = String(want.cc || '').toUpperCase();
  const rg = fold(want.region);
  const pop = r => Number(r.population) || 0;
  const score = r => (cc && String(r.country_code || '').toUpperCase() === cc ? 2 : 0) + (rg && fold(r.admin1) === rg ? 1 : 0) + (fold(r.name) === fold(want.name) ? 0.5 : 0);
  return list.slice().sort((a, b) => score(b) - score(a) || pop(b) - pop(a))[0];
}

/**
 * The language a place name is written in, by its script — the geocoder is asked
 * in it ('ar' for ديربورن, 'ru' for Москва). '' for Latin script (asked in English).
 */
export function scriptLang(name) {
  const t = String(name || '');
  if (/[\u0600-\u06FF\u0750-\u077F]/.test(t)) return 'ar';
  if (/[\u0590-\u05FF]/.test(t)) return 'he';
  if (/[\u0400-\u04FF]/.test(t)) return 'ru';
  if (/[\u0900-\u097F]/.test(t)) return 'hi';
  if (/[\u3040-\u30FF]/.test(t)) return 'ja';
  if (/[\uAC00-\uD7AF]/.test(t)) return 'ko';
  if (/[\u4E00-\u9FFF]/.test(t)) return 'zh';
  if (/[\u0E00-\u0E7F]/.test(t)) return 'th';
  if (/[\u0370-\u03FF]/.test(t)) return 'el';
  return '';
}

/**
 * Where to DRAW somebody inside their city: a fixed spot within `km` of the
 * centre, from their id. A whole city sharing one point is one dot; spread,
 * it reads as a city. It says nothing — the spot comes from the id, not from
 * where they are.
 */
export function spreadAround(lat, lng, seed, km = 2.5) {
  let h = 2166136261;
  const s = String(seed || '');
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const a = (h >>> 0) % 10000 / 10000 * Math.PI * 2;
  const r = Math.sqrt((h >>> 13 >>> 0) % 10000 / 10000) * km;
  const dLat = r * Math.cos(a) / 111;
  const dLng = r * Math.sin(a) / (111 * Math.max(0.15, Math.cos((Number(lat) || 0) * Math.PI / 180)));
  return {
    lat: lat + dLat,
    lng: lng + dLng
  };
}

/* ── the map's own words: place names in the reader's language ───────────── */

/** Languages written in Latin letters fall back to the Latin name, the rest to the local one. */
const LATIN = ['en', 'es', 'fr', 'de', 'pt', 'it', 'nl', 'tr', 'id', 'ms', 'pl', 'sv', 'da', 'no', 'fi', 'ro', 'cs', 'hu', 'vi', 'sw', 'tl'];
/**
 * The label a map style should draw for a place, in the reader's language:
 * "مصر" for an Arabic reader, "Egypt" for an English one — OpenStreetMap's own
 * name:xx, then (for Latin-script readers) the Latin name, then the local name.
 */
export function mapNameField(lang) {
  const l = String(lang || 'en').slice(0, 2).toLowerCase();
  const out = ['coalesce', ['get', `name:${l}`]];
  if (LATIN.indexOf(l) >= 0) out.push(['get', 'name:latin'], ['get', 'name_en']);
  out.push(['get', 'name']);
  return out;
}
/** Is a style's text field a place NAME (not a road number or a house number)? */
export function isNameLabel(textField) {
  const j = JSON.stringify(textField || '');
  return /name/.test(j) && !/housenumber|"ref"|\{ref\}/.test(j);
}

/** The relation the card's button is drawn from. */

export function mapRelation(o) {
  if (o.isSelf) return 'self';
  if (o.isFriend) return 'friend';
  if (o.sent) return 'sent';
  if (o.received) return 'received';
  return 'none';
}

/* ── geohash ─────────────────────────────────────────────────────────────── */

const B32 = '0123456789bcdefghjkmnpqrstuvwxyz';
export function isGeohash(s, min = 1, max = 12) {
  return typeof s === 'string' && s.length >= min && s.length <= max && /^[0-9b-hjkmnp-z]+$/.test(s);
}

/** Encode a position. Out-of-range values are clamped, never wrapped. */
export function geohashEncode(lat, lng, precision) {
  let la = Math.max(-90, Math.min(90, Number(lat) || 0));
  let lo = Number(lng) || 0;
  lo = ((lo + 180) % 360 + 360) % 360 - 180;
  la = Math.min(la, 89.999999);
  const latR = [-90, 90];
  const lngR = [-180, 180];
  let out = '';
  let bit = 0;
  let ch = 0;
  let even = true;
  const p = Math.max(1, Math.min(12, Math.floor(precision) || 1));
  while (out.length < p) {
    const r = even ? lngR : latR;
    const v = even ? lo : la;
    const mid = (r[0] + r[1]) / 2;
    if (v >= mid) {
      ch = ch << 1 | 1;
      r[0] = mid;
    } else {
      ch <<= 1;
      r[1] = mid;
    }
    even = !even;
    if (++bit === 5) {
      out += B32[ch];
      bit = 0;
      ch = 0;
    }
  }
  return out;
}

/** The cell's bounds and centre. */
export function geohashDecode(gh) {
  const latR = [-90, 90];
  const lngR = [-180, 180];
  let even = true;
  for (const c of String(gh || '')) {
    const v = B32.indexOf(c);
    if (v < 0) break;
    for (let b = 4; b >= 0; b--) {
      const r = even ? lngR : latR;
      const mid = (r[0] + r[1]) / 2;
      if (v >> b & 1) r[0] = mid;else r[1] = mid;
      even = !even;
    }
  }
  return {
    lat: (latR[0] + latR[1]) / 2,
    lng: (lngR[0] + lngR[1]) / 2,
    latErr: (latR[1] - latR[0]) / 2,
    lngErr: (lngR[1] - lngR[0]) / 2
  };
}

/** The cell and its eight neighbours (fewer at a pole), no duplicates. */
export function geohashNeighbours(gh) {
  const d = geohashDecode(gh);
  const out = [];
  for (const dy of [-1, 0, 1]) {
    for (const dx of [-1, 0, 1]) {
      const lat = d.lat + dy * d.latErr * 2;
      if (lat > 90 || lat < -90) continue;
      const g = geohashEncode(lat, d.lng + dx * d.lngErr * 2, gh.length);
      if (!out.includes(g)) out.push(g);
    }
  }
  return out;
}

/**
 * 🟥 THE ONLY POSITION THAT IS EVER STORED: the centre of the cell, rounded.
 * A position that is not a number is no position at all.
 */
export function snapToCell(lat, lng, precision) {
  const la = Number(lat);
  const lo = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(lo) || Math.abs(la) > 90 || Math.abs(lo) > 180) return null;
  if (la === 0 && lo === 0) return null; // "null island" is a failed fix, not a place
  const gh = geohashEncode(la, lo, precision);
  const c = geohashDecode(gh);
  const r = v => Math.round(v * 1e5) / 1e5;
  return {
    gh,
    lat: r(c.lat),
    lng: r(c.lng)
  };
}

/** A city or country label as stored: trimmed, one line, short. */
export function cleanPlace(s) {
  return typeof s === 'string' ? s.replace(/[\u0000-\u001f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 60) : '';
}

/* ── the far view: counts, merged to the zoom ────────────────────────────── */

/** n people, p pages, o of the people online now (and sharing it with everyone);
 *  lat/lng: where the cell's people and pages actually are (its centre of mass) */

/**
 * Merge cells to `precision` (≤ their own): counts add, the centre is the
 * count-weighted mean of unit vectors — an average of longitudes breaks at
 * the date line, an average of directions does not.
 * 🟥 Each cell stands where its PEOPLE are (lat/lng from the server) — never at
 *    the middle of its square, which can be 75 km from the only town in it.
 *    Only a cell without a centre of mass falls back to its middle.
 */
export function mergeCells(cells, precision) {
  const acc = new Map();
  for (const c of cells || []) {
    if (!c || !isGeohash(c.gh)) continue;
    const n = Math.max(0, Number(c.n) || 0);
    const p = Math.max(0, Number(c.p) || 0);
    if (n + p <= 0) continue;
    const key = c.gh.slice(0, Math.max(1, precision));
    const exact = Number.isFinite(Number(c.lat)) && Number.isFinite(Number(c.lng)) && c.lat !== null && c.lng !== null;
    const d = exact ? {
      lat: Number(c.lat),
      lng: Number(c.lng)
    } : geohashDecode(c.gh);
    const la = d.lat * Math.PI / 180;
    const lo = d.lng * Math.PI / 180;
    const w = n + p;
    const a = acc.get(key) || {
      x: 0,
      y: 0,
      z: 0,
      n: 0,
      p: 0,
      o: 0
    };
    a.x += Math.cos(la) * Math.cos(lo) * w;
    a.y += Math.cos(la) * Math.sin(lo) * w;
    a.z += Math.sin(la) * w;
    a.n += n;
    a.p += p;
    a.o += Math.min(n, Math.max(0, Number(c.o) || 0));
    acc.set(key, a);
  }
  const out = [];
  for (const [gh, a] of acc) {
    const len = Math.hypot(a.x, a.y, a.z) || 1;
    out.push({
      gh,
      lat: Math.asin(a.z / len) * 180 / Math.PI,
      lng: Math.atan2(a.y, a.x) * 180 / Math.PI,
      n: a.n,
      p: a.p,
      o: a.o
    });
  }
  return out.sort((x, y) => y.n + y.p - (x.n + x.p));
}

/** Dot size for a count: grows with the log, so a city is bigger than a village but not a thousand times. */
export function blobSize(count) {
  const n = Math.max(1, Number(count) || 1);
  return Math.min(4.2, 1 + Math.log10(n) * 0.95);
}

/* ── the map's zoom ─────────────────────────────────────────────────────
   The world map is a REAL map (Apple Maps on iOS, MapLibre on Android and
   the web): what it draws depends on the zoom level — the web
   map's zoom, and the same number worked out from the visible span on the
   phone (zoomForSpan). */

/** From here in, the near view lists people and neighbourhoods (~60 km across on a phone). */
export const MAP_NEAR_ZOOM = 9.5;
/** From here in, counts are per ~156 km cell; further out, per continent-sized cell. */
export const MAP_REGION_ZOOM = 4.5;
export function layerForZoom(zoom) {
  const z = Number(zoom) || 0;
  if (z >= MAP_NEAR_ZOOM) return 'near';
  if (z >= MAP_REGION_ZOOM) return 'region';
  return 'world';
}
export function precisionForLayer(layer) {
  return layer === 'world' ? 2 : MAP_CELL_PRECISION;
}
/** Where the map starts, where it settles over me on opening, the "whole world" button,
 *  a city (my location, a friend), and the zoom a tap on a crowd takes from each layer. */
export const MAP_START_ZOOM = 1.6;
export const MAP_HOME_ZOOM = 4.6;
export const MAP_WORLD_ZOOM = 1.8;
export const MAP_CITY_ZOOM = 12;
export function diveFrom(layer) {
  return layer === 'world' ? 6 : MAP_CITY_ZOOM;
}
export const MAP_MIN_ZOOM = 1;
export const MAP_MAX_ZOOM = 18;
export function clampZoom(z) {
  return Math.max(MAP_MIN_ZOOM, Math.min(MAP_MAX_ZOOM, Number.isFinite(Number(z)) ? Number(z) : MAP_MIN_ZOOM));
}

/** The web-map zoom for a visible span (degrees of longitude across `widthPx` points). */
export function zoomForSpan(lngDelta, widthPx) {
  const d = Math.max(1e-6, Math.min(360, Number(lngDelta) || 360));
  return clampZoom(Math.log2(360 * Math.max(1, widthPx || 1) / (256 * d)));
}
/** …and back: the span (lat and lng degrees) a zoom shows on a `w × h` screen at `lat`. */
export function spanForZoom(zoom, lat, widthPx, heightPx) {
  const z = clampZoom(zoom);
  const lngD = Math.min(360, 360 * Math.max(1, widthPx || 1) / (256 * Math.pow(2, z)));
  const latD = Math.min(170, lngD * (Math.max(1, heightPx || 1) / Math.max(1, widthPx || 1)) * Math.max(0.05, Math.cos((Number(lat) || 0) * Math.PI / 180)));
  return {
    latitudeDelta: latD,
    longitudeDelta: lngD
  };
}
/** Is a place inside a visible box (with a margin, and across the date line)? */
export function inView(lat, lng, v, margin = 0.25) {
  const hh = v.latitudeDelta * (0.5 + margin);
  const hw = v.longitudeDelta * (0.5 + margin);
  if (hw >= 180) return Math.abs(lat - v.lat) <= hh;
  let dl = Math.abs(lng - v.lng);
  if (dl > 180) dl = 360 - dl;
  return Math.abs(lat - v.lat) <= hh && dl <= hw;
}

/** The near view asks for this cell and its neighbours, from the point under the camera. */
export function nearPrefixes(lat, lng) {
  return geohashNeighbours(geohashEncode(lat, lng, MAP_NEAR_PRECISION));
}

/**
 * Where to DRAW an anonymous count inside its cell: the centre, nudged by a
 * fixed amount taken from the cell's own name. Cell centres sit on a perfect
 * grid and read as a scoreboard; nudged, they read as a town. It reveals
 * nothing — the nudge comes from the cell, not from anyone in it, and stays
 * inside the cell.
 */
export function jitterInCell(gh) {
  const d = geohashDecode(gh);
  let h = 2166136261;
  for (let i = 0; i < gh.length; i++) {
    h ^= gh.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const a = (h >>> 0) % 1000 / 1000;
  const b = (h >>> 10 >>> 0) % 1000 / 1000;
  return {
    lat: d.lat + (a - 0.5) * 1.4 * d.latErr,
    lng: d.lng + (b - 0.5) * 1.4 * d.lngErr
  };
}

/** Great-circle distance in km (for "3 km away"). */
export function distanceKm(aLat, aLng, bLat, bLng) {
  const r = Math.PI / 180;
  const dLa = (bLat - aLat) * r;
  const dLo = (bLng - aLng) * r;
  const h = Math.sin(dLa / 2) ** 2 + Math.cos(aLat * r) * Math.cos(bLat * r) * Math.sin(dLo / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
}

/* ── the sun ─────────────────────────────────────────────────────────────── */

/** Where the sun is overhead right now (degrees) — the globe is lit from here. */
export function subsolarPoint(ms) {
  const d = (Number(ms) || 0) / 86400000 + 2440587.5 - 2451545.0;
  const rad = Math.PI / 180;
  const g = (357.529 + 0.98560028 * d) * rad;
  const q = 280.459 + 0.98564736 * d;
  const L = (q + 1.915 * Math.sin(g) + 0.020 * Math.sin(2 * g)) * rad;
  const e = (23.439 - 0.00000036 * d) * rad;
  const ra = Math.atan2(Math.cos(e) * Math.sin(L), Math.cos(L)) / rad;
  const decl = Math.asin(Math.sin(e) * Math.sin(L)) / rad;
  let eqt = ((q - ra) % 360 + 540) % 360 - 180; // degrees, −180..180
  eqt /= 15; // hours
  const utcH = ((Number(ms) || 0) % 86400000 + 86400000) % 86400000 / 3600000;
  let lng = -15 * (utcH - 12 + eqt);
  lng = ((lng + 180) % 360 + 360) % 360 - 180;
  return {
    lat: decl,
    lng
  };
}

/** The sun's height above the horizon at a place (degrees), and whether it is the morning side. */
export function sunAt(ms, lat, lng) {
  const s = subsolarPoint(ms);
  const r = Math.PI / 180;
  let h = (lng - s.lng + 540) % 360 - 180; // hour angle, degrees
  const sinEl = Math.sin(lat * r) * Math.sin(s.lat * r) + Math.cos(lat * r) * Math.cos(s.lat * r) * Math.cos(h * r);
  return {
    elevation: Math.asin(Math.max(-1, Math.min(1, sinEl))) / r,
    morning: h < 0
  };
}
/** The phase of the sky from the sun's height: twilight below −4°, golden hour up to 8°. */
export function skyPhase(elevation, morning) {
  if (elevation < -12) return 'night';
  if (elevation < -4) return morning ? 'dawn' : 'dusk';
  if (elevation < 8) return morning ? 'sunrise' : 'sunset';
  return 'day';
}

/** Today's sunrise and sunset at a place (ms, UTC), or null for polar day / night. */
export function sunTimes(ms, lat, lng) {
  const noonGuess = Math.floor(ms / 86400000) * 86400000 + 43200000 - lng / 15 * 3600000;
  const s = subsolarPoint(noonGuess);
  const r = Math.PI / 180;
  const cosH = (Math.sin(-0.833 * r) - Math.sin(lat * r) * Math.sin(s.lat * r)) / (Math.cos(lat * r) * Math.cos(s.lat * r));
  if (cosH < -1 || cosH > 1) return null;
  const H = Math.acos(cosH) / r; // degrees
  /* solar noon: when the subsolar longitude is this longitude */
  let noon = noonGuess + ((s.lng - lng + 540) % 360 - 180) / 15 * 3600000;
  noon = Math.round(noon);
  return {
    sunrise: Math.round(noon - H / 15 * 3600000),
    sunset: Math.round(noon + H / 15 * 3600000)
  };
}

/* ── the weather ─────────────────────────────────────────────────────────── */

/** WMO weather code (what the weather service reports) → what the sky shows. */
export function weatherKind(code) {
  const c = Number(code);
  if (!Number.isFinite(c)) return 'clear';
  if (c === 0) return 'clear';
  if (c === 1 || c === 2) return 'partly';
  if (c === 3) return 'clouds';
  if (c === 45 || c === 48) return 'fog';
  if (c >= 51 && c <= 57) return 'drizzle';
  if (c >= 61 && c <= 67 || c >= 80 && c <= 82) return 'rain';
  if (c >= 71 && c <= 77 || c === 85 || c === 86) return 'snow';
  if (c >= 95) return 'storm';
  return 'clear';
}
const SKY = {
  night: ['#02050F', '#061230', '#0D1E45'],
  dawn: ['#0A1434', '#3B2C5F', '#B8676E'],
  sunrise: ['#29407E', '#E98A63', '#FFD58A'],
  day: ['#1F6FD6', '#5EA8F0', '#C9E5FF'],
  sunset: ['#29215A', '#B3436E', '#F4A05B'],
  dusk: ['#0B1233', '#3A2558', '#7A3D63']
};
const hex = h => [1, 3, 5].map(i => parseInt(h.slice(i, i + 2), 16));
const toHex = c => '#' + c.map(v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('').toUpperCase();
/** Mix two colours: t = 0 is `a`, 1 is `b`. */
export function mixHex(a, b, t) {
  const x = hex(a);
  const y = hex(b);
  const k = Math.max(0, Math.min(1, t));
  return toHex(x.map((v, i) => v + (y[i] - v) * k));
}
/** Everything the sky behind the globe draws, from the phase and the weather. */
export function skyScene(phase, weather) {
  const dark = phase === 'night' || phase === 'dusk' || phase === 'dawn';
  const grey = dark ? '#151A24' : '#6B7685';
  const veil = {
    clear: 0,
    partly: 0.12,
    clouds: 0.42,
    fog: 0.5,
    drizzle: 0.5,
    rain: 0.62,
    snow: 0.45,
    storm: 0.78
  };
  const target = weather === 'snow' && !dark ? '#B9C6D3' : weather === 'fog' && !dark ? '#9AA5B1' : grey;
  const base = SKY[phase];
  const colors = base.map((c, i) => mixHex(c, target, veil[weather] * (i === 0 ? 0.85 : 1)));
  const clouds = {
    clear: 0,
    partly: 0.35,
    clouds: 0.8,
    fog: 0.3,
    drizzle: 0.75,
    rain: 0.9,
    snow: 0.7,
    storm: 1
  }[weather];
  const particles = weather === 'rain' || weather === 'drizzle' || weather === 'storm' ? 'rain' : weather === 'snow' ? 'snow' : null;
  const density = weather === 'drizzle' ? 0.35 : weather === 'rain' ? 0.75 : weather === 'storm' ? 1 : weather === 'snow' ? 0.7 : 0;
  const starsBase = phase === 'night' ? 1 : phase === 'dusk' || phase === 'dawn' ? 0.45 : 0;
  const lum = h => {
    const [r, g, b] = hex(h);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  return {
    phase,
    weather,
    colors,
    stars: Math.max(0, starsBase * (1 - clouds * 0.9)),
    sun: phase === 'day' || phase === 'sunrise' || phase === 'sunset' ? Math.max(0, 1 - clouds * 0.85) : 0,
    moon: phase === 'night' || phase === 'dusk' ? Math.max(0, 1 - clouds * 0.8) : 0,
    clouds,
    particles,
    density,
    lightning: weather === 'storm',
    fog: weather === 'fog' ? 0.55 : 0,
    ink: lum(colors[0]) > 150 ? '#0B1020' : '#FFFFFF'
  };
}

/** The sky with no weather and no position — the device clock alone (permission refused, offline). */
export function fallbackPhase(localHour) {
  const h = Number(localHour) || 0;
  if (h < 5 || h >= 21) return 'night';
  if (h < 6) return 'dawn';
  if (h < 8) return 'sunrise';
  if (h < 18) return 'day';
  if (h < 20) return 'sunset';
  return 'dusk';
}

/* ── temperature: °F in the United States, °C everywhere else ──────────── */

/** The US and its territories read Fahrenheit; everybody else reads Celsius. */
const FAHRENHEIT_CC = ['US', 'PR', 'GU', 'VI', 'AS', 'MP', 'UM'];

/* ── the lights of the cities ────────────────────────────────────────────────
   At night the map's cities glow — a warm light over every city and town,
   brightest where the most people live, the built-up streets lit amber close
   in, and windows in the buildings at street level. In twilight they come on;
   at the golden hours there is a first hint of them; by day, none.
   The layers are MapLibre style layers over the map's own vector tiles
   (OpenFreeMap / OpenMapTiles: `place`, `landuse`, `building`) — the phone's
   page and the browser draw the same ones. */

/** How strongly the cities glow at this hour: 0 (day) … 1 (night). */
export function cityLightLevel(phase) {
  if (phase === 'night') return 1;
  if (phase === 'dusk' || phase === 'dawn') return 0.8;
  if (phase === 'sunset' || phase === 'sunrise') return 0.35;
  return 0;
}

/** The ids of the light layers (removed and re-added as the hour changes). */
export const CITY_LIGHT_IDS = ['wm-lights-urban', 'wm-lights-windows', 'wm-lights-glow'];

/** The light layers for a glow level, over the vector source `source`. None at 0. */
export function cityLightLayers(level, source) {
  const k = Math.max(0, Math.min(1, Number(level) || 0));
  if (k <= 0 || !source) return [];
  const r2 = v => Math.round(v * 100) / 100;
  return [{
    id: 'wm-lights-urban',
    type: 'fill',
    source,
    'source-layer': 'landuse',
    minzoom: 6,
    filter: ['in', ['get', 'class'], ['literal', ['residential', 'commercial', 'industrial', 'retail', 'neighbourhood', 'suburb']]],
    paint: {
      'fill-color': '#FFB65C',
      'fill-antialias': false,
      'fill-opacity': ['interpolate', ['linear'], ['zoom'], 6, r2(0.08 * k), 10, r2(0.22 * k), 15, r2(0.12 * k)]
    }
  }, {
    id: 'wm-lights-windows',
    type: 'fill',
    source,
    'source-layer': 'building',
    minzoom: 13,
    paint: {
      'fill-color': '#FFD889',
      'fill-opacity': ['interpolate', ['linear'], ['zoom'], 13, 0, 14.5, r2(0.34 * k)]
    }
  }, {
    id: 'wm-lights-glow',
    type: 'heatmap',
    source,
    'source-layer': 'place',
    maxzoom: 15,
    filter: ['in', ['get', 'class'], ['literal', ['city', 'town', 'village']]],
    paint: {
      'heatmap-weight': ['match', ['get', 'class'], 'city', 1, 'town', 0.5, 0.16],
      'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 0.6, 6, 1.2, 10, 2],
      'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 6, 3, 14, 6, 30, 9, 55, 12, 80],
      'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'], 0, 'rgba(255,160,60,0)', 0.2, 'rgba(255,150,50,0.28)', 0.45, 'rgba(255,184,90,0.55)', 0.7, 'rgba(255,214,140,0.78)', 1, 'rgba(255,240,205,0.92)'],
      'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 0, r2(0.85 * k), 11, r2(0.7 * k), 14, r2(0.2 * k)]
    }
  }];
}

/** City, town and country names: brighter and haloed so they read over the lights (and the sky). */
export function placeLabelPaint(dark) {
  return dark ? {
    'text-color': '#FFF4DE',
    'text-halo-color': 'rgba(8,10,22,0.92)',
    'text-halo-width': 1.6,
    'text-halo-blur': 0.4
  } : {
    'text-color': '#121A2A',
    'text-halo-color': 'rgba(255,255,255,0.96)',
    'text-halo-width': 1.8,
    'text-halo-blur': 0.3
  };
}

/** The country a locale names ("en-US" → "US", "ar_SA" → "SA"), or ''. */
export function localeCountry(locale) {
  const m = /[-_]([A-Za-z]{2})(?:$|[-_@.])/.exec(String(locale || ''));
  return m ? m[1].toUpperCase() : '';
}

/**
 * Fahrenheit or Celsius for this viewer: the country of their place on the map
 * first (somebody in Dearborn reads °F even with an Arabic phone); with no place
 * yet, the country their device is set to.
 */
export function usesFahrenheit(placeCc, locale) {
  const cc = String(placeCc || '').toUpperCase() || localeCountry(locale);
  return FAHRENHEIT_CC.indexOf(cc) >= 0;
}

/** 22 (°C) → "72°F" or "22°C". */
export function formatTemp(celsius, fahrenheit) {
  const c = Number(celsius);
  if (!Number.isFinite(c)) return '';
  return fahrenheit ? `${Math.round(c * 9 / 5 + 32)}°F` : `${Math.round(c)}°C`;
}

/** The device's own locale ("en-US"), wherever this runs — '' when it cannot say. */
export function deviceLocale() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().locale || '';
  } catch {
    return '';
  }
}

/* ── pages ───────────────────────────────────────────────────────────────── */

/** A page's category → what the card calls it, and its pin colour. */
const PAGE_KIND = {
  shop: {
    key: 'kind_store',
    color: '#FF9F0A'
  },
  business: {
    key: 'kind_business',
    color: '#4285F4'
  },
  restaurant: {
    key: 'kind_restaurant',
    color: '#FF6B35'
  },
  creator: {
    key: 'kind_creator',
    color: '#AF52DE'
  },
  public_figure: {
    key: 'kind_public_figure',
    color: '#AF52DE'
  },
  sport: {
    key: 'kind_sport',
    color: '#34C759'
  },
  education: {
    key: 'kind_education',
    color: '#5E5CE6'
  },
  health: {
    key: 'kind_health',
    color: '#FF375F'
  },
  technology: {
    key: 'kind_tech',
    color: '#0A84FF'
  },
  news: {
    key: 'kind_news',
    color: '#8E8E93'
  },
  entertainment: {
    key: 'kind_entertainment',
    color: '#FF2D92'
  },
  community: {
    key: 'kind_community',
    color: '#30B0C7'
  }
};
export function pageKind(category) {
  return PAGE_KIND[String(category || '')] || {
    key: 'kind_page',
    color: '#FFB020'
  };
}

/** The pin colour of a person's dot. */
export const MAP_GREEN = '#34E39A';
export const MAP_FRIEND = '#5AC8FA';
export const MAP_SELF = '#FFFFFF';
/** Somebody who is not online right now (or does not share it): on the map, but quiet. */
export const MAP_OFFLINE = '#8C97AD';
/** A dot's colour: green when online, slate when not. */
export const dotColor = online => online ? MAP_GREEN : MAP_OFFLINE;

/* ── words ───────────────────────────────────────────────────────────────── */

const W = {
  world_map: {
    en: 'World map',
    ar: 'خريطة العالم',
    es: 'Mapa del mundo',
    fr: 'Carte du monde',
    de: 'Weltkarte',
    pt: 'Mapa-múndi',
    ru: 'Карта мира',
    hi: 'विश्व मानचित्र',
    ja: '世界地図',
    zh: '世界地图'
  },
  world_sub: {
    en: 'See where Allo is, live',
    ar: 'شاهد أين يتواجد ألو مباشرةً',
    es: 'Mira dónde está Allo, en vivo',
    fr: 'Voyez où est Allo, en direct',
    de: 'Sieh live, wo Allo ist',
    pt: 'Veja onde o Allo está, ao vivo',
    ru: 'Где сейчас Allo — вживую',
    hi: 'देखें Allo कहाँ है, लाइव',
    ja: 'Alloの今をライブで',
    zh: '实时查看 Allo 的足迹'
  },
  people_n: {
    en: '{n} people',
    ar: '{n} شخص',
    es: '{n} personas',
    fr: '{n} personnes',
    de: '{n} Personen',
    pt: '{n} pessoas',
    ru: '{n} чел.',
    hi: '{n} लोग',
    ja: '{n}人',
    zh: '{n} 人'
  },
  pages_n: {
    en: '{n} pages',
    ar: '{n} صفحة',
    es: '{n} páginas',
    fr: '{n} pages',
    de: '{n} Seiten',
    pt: '{n} páginas',
    ru: '{n} стр.',
    hi: '{n} पेज',
    ja: '{n}ページ',
    zh: '{n} 个主页'
  },
  you: {
    en: 'You',
    ar: 'أنت',
    es: 'Tú',
    fr: 'Vous',
    de: 'Du',
    pt: 'Você',
    ru: 'Вы',
    hi: 'आप',
    ja: 'あなた',
    zh: '你'
  },
  friends_on_map: {
    en: 'Friends on the map',
    ar: 'أصدقاء على الخريطة',
    es: 'Amigos en el mapa',
    fr: 'Amis sur la carte',
    de: 'Freunde auf der Karte',
    pt: 'Amigos no mapa',
    ru: 'Друзья на карте',
    hi: 'मानचित्र पर मित्र',
    ja: '地図上の友達',
    zh: '地图上的好友'
  },
  add_friend: {
    en: 'Add friend',
    ar: 'إضافة صديق',
    es: 'Agregar amigo',
    fr: 'Ajouter en ami',
    de: 'Als Freund hinzufügen',
    pt: 'Adicionar amigo',
    ru: 'Добавить в друзья',
    hi: 'मित्र जोड़ें',
    ja: '友達に追加',
    zh: '加为好友'
  },
  requested: {
    en: 'Requested',
    ar: 'تم الإرسال',
    es: 'Solicitado',
    fr: 'Demande envoyée',
    de: 'Angefragt',
    pt: 'Solicitado',
    ru: 'Запрос отправлен',
    hi: 'अनुरोध भेजा',
    ja: 'リクエスト済み',
    zh: '已发送'
  },
  friends: {
    en: 'Friends',
    ar: 'أصدقاء',
    es: 'Amigos',
    fr: 'Amis',
    de: 'Freunde',
    pt: 'Amigos',
    ru: 'Друзья',
    hi: 'मित्र',
    ja: '友達',
    zh: '好友'
  },
  respond: {
    en: 'Respond',
    ar: 'رد',
    es: 'Responder',
    fr: 'Répondre',
    de: 'Antworten',
    pt: 'Responder',
    ru: 'Ответить',
    hi: 'जवाब दें',
    ja: '返信',
    zh: '回应'
  },
  follow: {
    en: 'Follow',
    ar: 'متابعة',
    es: 'Seguir',
    fr: 'Suivre',
    de: 'Folgen',
    pt: 'Seguir',
    ru: 'Подписаться',
    hi: 'फ़ॉलो करें',
    ja: 'フォロー',
    zh: '关注'
  },
  following: {
    en: 'Following',
    ar: 'تتابعه',
    es: 'Siguiendo',
    fr: 'Abonné',
    de: 'Folgst du',
    pt: 'Seguindo',
    ru: 'Вы подписаны',
    hi: 'फ़ॉलो कर रहे हैं',
    ja: 'フォロー中',
    zh: '已关注'
  },
  followers_n: {
    en: '{n} followers',
    ar: '{n} متابع',
    es: '{n} seguidores',
    fr: '{n} abonnés',
    de: '{n} Follower',
    pt: '{n} seguidores',
    ru: '{n} подписчиков',
    hi: '{n} फ़ॉलोअर',
    ja: 'フォロワー{n}人',
    zh: '{n} 位关注者'
  },
  view_profile: {
    en: 'View profile',
    ar: 'عرض الملف',
    es: 'Ver perfil',
    fr: 'Voir le profil',
    de: 'Profil ansehen',
    pt: 'Ver perfil',
    ru: 'Профиль',
    hi: 'प्रोफ़ाइल देखें',
    ja: 'プロフィール',
    zh: '查看主页'
  },
  view_page: {
    en: 'View page',
    ar: 'عرض الصفحة',
    es: 'Ver página',
    fr: 'Voir la page',
    de: 'Seite ansehen',
    pt: 'Ver página',
    ru: 'Страница',
    hi: 'पेज देखें',
    ja: 'ページを見る',
    zh: '查看主页'
  },
  km_away: {
    en: '{n} km away',
    ar: 'على بعد {n} كم',
    es: 'a {n} km',
    fr: 'à {n} km',
    de: '{n} km entfernt',
    pt: 'a {n} km',
    ru: 'в {n} км',
    hi: '{n} किमी दूर',
    ja: '{n} km先',
    zh: '{n} 公里外'
  },
  near_you: {
    en: 'Near you',
    ar: 'بالقرب منك',
    es: 'Cerca de ti',
    fr: 'Près de vous',
    de: 'In deiner Nähe',
    pt: 'Perto de você',
    ru: 'Рядом с вами',
    hi: 'आपके पास',
    ja: '近く',
    zh: '在你附近'
  },
  someone_here: {
    en: '{n} people here',
    ar: '{n} أشخاص هنا',
    es: '{n} personas aquí',
    fr: '{n} personnes ici',
    de: '{n} Personen hier',
    pt: '{n} pessoas aqui',
    ru: 'Здесь {n} чел.',
    hi: 'यहाँ {n} लोग',
    ja: 'ここに{n}人',
    zh: '这里有 {n} 人'
  },
  private_dot: {
    en: 'This person keeps their name private',
    ar: 'هذا الشخص يُخفي اسمه',
    es: 'Esta persona mantiene su nombre privado',
    fr: 'Cette personne garde son nom privé',
    de: 'Diese Person hält ihren Namen privat',
    pt: 'Esta pessoa mantém o nome privado',
    ru: 'Этот человек скрывает имя',
    hi: 'यह व्यक्ति अपना नाम निजी रखता है',
    ja: 'この人は名前を非公開にしています',
    zh: '此人未公开姓名'
  },
  zoom_in_more: {
    en: 'Zoom in to see who is here',
    ar: 'قرّب لترى من هنا',
    es: 'Acércate para ver quién está aquí',
    fr: 'Zoomez pour voir qui est là',
    de: 'Zoome hinein, um zu sehen, wer hier ist',
    pt: 'Aproxime para ver quem está aqui',
    ru: 'Приблизьте, чтобы увидеть, кто здесь',
    hi: 'कौन है देखने के लिए ज़ूम करें',
    ja: 'ズームして誰がいるか見る',
    zh: '放大查看这里有谁'
  },
  map_satellite: {
    en: 'Satellite',
    ar: 'قمر صناعي',
    es: 'Satélite',
    fr: 'Satellite',
    de: 'Satellit',
    pt: 'Satélite',
    ru: 'Спутник',
    hi: 'उपग्रह',
    ja: '航空写真',
    zh: '卫星'
  },
  map_streets: {
    en: 'Map',
    ar: 'خريطة',
    es: 'Mapa',
    fr: 'Plan',
    de: 'Karte',
    pt: 'Mapa',
    ru: 'Карта',
    hi: 'मानचित्र',
    ja: '地図',
    zh: '地图'
  },
  map_offline: {
    en: "The map couldn't load",
    ar: 'تعذّر تحميل الخريطة',
    es: 'No se pudo cargar el mapa',
    fr: "La carte n'a pas pu se charger",
    de: 'Die Karte konnte nicht geladen werden',
    pt: 'Não foi possível carregar o mapa',
    ru: 'Не удалось загрузить карту',
    hi: 'मानचित्र लोड नहीं हो सका',
    ja: '地図を読み込めませんでした',
    zh: '地图无法加载'
  },
  map_retry: {
    en: 'Try again',
    ar: 'حاول مرة أخرى',
    es: 'Reintentar',
    fr: 'Réessayer',
    de: 'Erneut versuchen',
    pt: 'Tentar novamente',
    ru: 'Повторить',
    hi: 'फिर से कोशिश करें',
    ja: '再試行',
    zh: '重试'
  },
  my_location: {
    en: 'My location',
    ar: 'موقعي',
    es: 'Mi ubicación',
    fr: 'Ma position',
    de: 'Mein Standort',
    pt: 'Minha localização',
    ru: 'Моё место',
    hi: 'मेरा स्थान',
    ja: '現在地',
    zh: '我的位置'
  },
  share_location: {
    en: 'Show me on the map',
    ar: 'أظهرني على الخريطة',
    es: 'Mostrarme en el mapa',
    fr: 'M’afficher sur la carte',
    de: 'Mich auf der Karte zeigen',
    pt: 'Mostrar-me no mapa',
    ru: 'Показать меня на карте',
    hi: 'मुझे मानचित्र पर दिखाएँ',
    ja: '地図に自分を表示',
    zh: '在地图上显示我'
  },
  location_why: {
    en: 'Your city is shown — the map never uses your GPS.',
    ar: 'تظهر مدينتك فقط — الخريطة لا تستخدم موقعك الجغرافي أبدًا.',
    es: 'Se muestra tu ciudad: el mapa nunca usa tu GPS.',
    fr: 'Votre ville est affichée — la carte n’utilise jamais votre GPS.',
    de: 'Gezeigt wird deine Stadt – die Karte nutzt nie dein GPS.',
    pt: 'Sua cidade é mostrada — o mapa nunca usa seu GPS.',
    ru: 'Показывается ваш город — карта никогда не использует GPS.',
    hi: 'आपका शहर दिखता है — मानचित्र कभी आपका GPS इस्तेमाल नहीं करता।',
    ja: '表示されるのは都市だけ。地図がGPSを使うことはありません。',
    zh: '只显示你的城市——地图从不使用你的 GPS。'
  },
  add_city_title: {
    en: 'Put yourself on the map',
    ar: 'ضع نفسك على الخريطة',
    es: 'Ponte en el mapa',
    fr: 'Placez-vous sur la carte',
    de: 'Zeig dich auf der Karte',
    pt: 'Coloque-se no mapa',
    ru: 'Отметьтесь на карте',
    hi: 'खुद को मानचित्र पर लाएँ',
    ja: '地図に自分を載せよう',
    zh: '把自己放上地图'
  },
  add_city_sub: {
    en: 'Add the city you live in. It stays put when you travel, and you can change it any time.',
    ar: 'أضف المدينة التي تعيش فيها. تبقى ثابتة عندما تسافر، ويمكنك تغييرها في أي وقت.',
    es: 'Añade la ciudad donde vives. No cambia cuando viajas y puedes cambiarla cuando quieras.',
    fr: 'Ajoutez la ville où vous vivez. Elle ne bouge pas quand vous voyagez, et vous pouvez la changer à tout moment.',
    de: 'Füge die Stadt hinzu, in der du lebst. Sie bleibt, wenn du reist, und du kannst sie jederzeit ändern.',
    pt: 'Adicione a cidade onde você mora. Ela não muda quando você viaja e pode ser alterada quando quiser.',
    ru: 'Добавьте город, где вы живёте. Он не меняется в поездках, и его можно сменить в любой момент.',
    hi: 'वह शहर जोड़ें जहाँ आप रहते हैं। यात्रा में यह नहीं बदलता, और आप इसे कभी भी बदल सकते हैं।',
    ja: '住んでいる都市を追加してください。旅行中も変わらず、いつでも変更できます。',
    zh: '添加你居住的城市。旅行时它不会变，你可以随时更改。'
  },
  add_city: {
    en: 'Add my city',
    ar: 'أضف مدينتي',
    es: 'Añadir mi ciudad',
    fr: 'Ajouter ma ville',
    de: 'Meine Stadt hinzufügen',
    pt: 'Adicionar minha cidade',
    ru: 'Добавить город',
    hi: 'मेरा शहर जोड़ें',
    ja: '都市を追加',
    zh: '添加我的城市'
  },
  search_city: {
    en: 'Search a city',
    ar: 'ابحث عن مدينة',
    es: 'Busca una ciudad',
    fr: 'Rechercher une ville',
    de: 'Stadt suchen',
    pt: 'Buscar uma cidade',
    ru: 'Найти город',
    hi: 'शहर खोजें',
    ja: '都市を検索',
    zh: '搜索城市'
  },
  no_cities: {
    en: 'No cities found',
    ar: 'لم يتم العثور على مدن',
    es: 'No se encontraron ciudades',
    fr: 'Aucune ville trouvée',
    de: 'Keine Städte gefunden',
    pt: 'Nenhuma cidade encontrada',
    ru: 'Города не найдены',
    hi: 'कोई शहर नहीं मिला',
    ja: '都市が見つかりません',
    zh: '未找到城市'
  },
  my_place: {
    en: 'My place on the map',
    ar: 'مكاني على الخريطة',
    es: 'Mi lugar en el mapa',
    fr: 'Ma place sur la carte',
    de: 'Mein Ort auf der Karte',
    pt: 'Meu lugar no mapa',
    ru: 'Моё место на карте',
    hi: 'मानचित्र पर मेरी जगह',
    ja: '地図上の自分の場所',
    zh: '我在地图上的位置'
  },
  change: {
    en: 'Change',
    ar: 'تغيير',
    es: 'Cambiar',
    fr: 'Modifier',
    de: 'Ändern',
    pt: 'Alterar',
    ru: 'Изменить',
    hi: 'बदलें',
    ja: '変更',
    zh: '更改'
  },
  from_profile: {
    en: 'From your profile',
    ar: 'من ملفك الشخصي',
    es: 'De tu perfil',
    fr: 'Depuis votre profil',
    de: 'Aus deinem Profil',
    pt: 'Do seu perfil',
    ru: 'Из профиля',
    hi: 'आपकी प्रोफ़ाइल से',
    ja: 'プロフィールから',
    zh: '来自你的资料'
  },
  use_profile_city: {
    en: 'Use my profile city: {city}',
    ar: 'استخدم مدينة ملفي: {city}',
    es: 'Usar la ciudad de mi perfil: {city}',
    fr: 'Utiliser la ville de mon profil : {city}',
    de: 'Stadt aus meinem Profil: {city}',
    pt: 'Usar a cidade do meu perfil: {city}',
    ru: 'Город из профиля: {city}',
    hi: 'प्रोफ़ाइल का शहर इस्तेमाल करें: {city}',
    ja: 'プロフィールの都市を使う：{city}',
    zh: '使用资料中的城市：{city}'
  },
  online_now_n: {
    en: '{n} online now',
    ar: '{n} متصل الآن',
    es: '{n} en línea ahora',
    fr: '{n} en ligne',
    de: '{n} jetzt online',
    pt: '{n} online agora',
    ru: '{n} в сети',
    hi: '{n} अभी ऑनलाइन',
    ja: '{n}人がオンライン',
    zh: '{n} 人在线'
  },
  online: {
    en: 'Online',
    ar: 'متصل',
    es: 'En línea',
    fr: 'En ligne',
    de: 'Online',
    pt: 'Online',
    ru: 'В сети',
    hi: 'ऑनलाइन',
    ja: 'オンライン',
    zh: '在线'
  },
  offline: {
    en: 'Offline',
    ar: 'غير متصل',
    es: 'Desconectado',
    fr: 'Hors ligne',
    de: 'Offline',
    pt: 'Offline',
    ru: 'Не в сети',
    hi: 'ऑफ़लाइन',
    ja: 'オフライン',
    zh: '离线'
  },
  show_online: {
    en: 'Show when I’m online',
    ar: 'إظهار عندما أكون متصلاً',
    es: 'Mostrar cuando estoy en línea',
    fr: 'Montrer quand je suis en ligne',
    de: 'Zeigen, wenn ich online bin',
    pt: 'Mostrar quando estou online',
    ru: 'Показывать, когда я в сети',
    hi: 'दिखाएँ जब मैं ऑनलाइन हूँ',
    ja: 'オンライン状態を表示',
    zh: '显示我在线'
  },
  on_everyone: {
    en: 'Everyone',
    ar: 'الجميع',
    es: 'Todos',
    fr: 'Tout le monde',
    de: 'Alle',
    pt: 'Todos',
    ru: 'Все',
    hi: 'सभी',
    ja: '全員',
    zh: '所有人'
  },
  on_friends: {
    en: 'Friends',
    ar: 'الأصدقاء',
    es: 'Amigos',
    fr: 'Amis',
    de: 'Freunde',
    pt: 'Amigos',
    ru: 'Друзья',
    hi: 'मित्र',
    ja: '友達',
    zh: '好友'
  },
  on_nobody: {
    en: 'No one — appear offline',
    ar: 'لا أحد — الظهور غير متصل',
    es: 'Nadie: aparecer desconectado',
    fr: 'Personne — apparaître hors ligne',
    de: 'Niemand – offline erscheinen',
    pt: 'Ninguém — aparecer offline',
    ru: 'Никто — быть не в сети',
    hi: 'कोई नहीं — ऑफ़लाइन दिखें',
    ja: '誰にも — オフライン表示',
    zh: '无人 — 显示为离线'
  },
  on_everyone_sub: {
    en: 'Your dot turns green for everyone while you’re on Allo.',
    ar: 'تصبح نقطتك خضراء للجميع أثناء استخدامك ألو.',
    es: 'Tu punto se vuelve verde para todos mientras usas Allo.',
    fr: 'Votre point devient vert pour tous quand vous êtes sur Allo.',
    de: 'Dein Punkt leuchtet für alle grün, während du in Allo bist.',
    pt: 'Seu ponto fica verde para todos enquanto você está no Allo.',
    ru: 'Ваша точка зелёная для всех, пока вы в Allo.',
    hi: 'Allo पर रहते हुए आपका बिंदु सबके लिए हरा होता है।',
    ja: 'Alloを使っている間、全員にあなたの点が緑に見えます。',
    zh: '你使用 Allo 时，所有人都能看到你的点变绿。'
  },
  on_friends_sub: {
    en: 'Only friends see you light up green. Everyone else sees you offline.',
    ar: 'يراك الأصدقاء فقط باللون الأخضر، ويراك الآخرون غير متصل.',
    es: 'Solo tus amigos te ven en verde. Los demás te ven desconectado.',
    fr: 'Seuls vos amis vous voient en vert. Les autres vous voient hors ligne.',
    de: 'Nur Freunde sehen dich grün. Alle anderen sehen dich offline.',
    pt: 'Só amigos veem você em verde. Os outros veem você offline.',
    ru: 'Зелёным вас видят только друзья, остальные — не в сети.',
    hi: 'सिर्फ़ मित्र आपको हरा देखते हैं। बाकी सबको आप ऑफ़लाइन दिखते हैं।',
    ja: '緑に見えるのは友達だけ。他の人にはオフラインに見えます。',
    zh: '只有好友看到你变绿，其他人看到你离线。'
  },
  on_nobody_sub: {
    en: 'You’re on the map, but always shown offline.',
    ar: 'أنت على الخريطة، لكنك تظهر دائمًا غير متصل.',
    es: 'Estás en el mapa, pero siempre desconectado.',
    fr: 'Vous êtes sur la carte, mais toujours hors ligne.',
    de: 'Du bist auf der Karte, aber immer offline.',
    pt: 'Você está no mapa, mas sempre offline.',
    ru: 'Вы на карте, но всегда не в сети.',
    hi: 'आप मानचित्र पर हैं, पर हमेशा ऑफ़लाइन दिखते हैं।',
    ja: '地図には載りますが、常にオフライン表示です。',
    zh: '你在地图上，但总是显示离线。'
  },
  who_sees: {
    en: 'Who sees me on the map',
    ar: 'من يراني على الخريطة',
    es: 'Quién me ve en el mapa',
    fr: 'Qui me voit sur la carte',
    de: 'Wer mich auf der Karte sieht',
    pt: 'Quem me vê no mapa',
    ru: 'Кто видит меня на карте',
    hi: 'मानचित्र पर मुझे कौन देखता है',
    ja: '地図で自分を見られる人',
    zh: '谁能在地图上看到我'
  },
  vis_everyone: {
    en: 'Public',
    ar: 'عام',
    es: 'Público',
    fr: 'Public',
    de: 'Öffentlich',
    pt: 'Público',
    ru: 'Все',
    hi: 'सार्वजनिक',
    ja: '公開',
    zh: '公开'
  },
  vis_friends: {
    en: 'Friends',
    ar: 'الأصدقاء',
    es: 'Amigos',
    fr: 'Amis',
    de: 'Freunde',
    pt: 'Amigos',
    ru: 'Друзья',
    hi: 'मित्र',
    ja: '友達',
    zh: '好友'
  },
  vis_anon: {
    en: 'Dot only',
    ar: 'نقطة فقط',
    es: 'Solo un punto',
    fr: 'Point seul',
    de: 'Nur Punkt',
    pt: 'Só um ponto',
    ru: 'Только точка',
    hi: 'केवल बिंदु',
    ja: '点のみ',
    zh: '仅圆点'
  },
  vis_nobody: {
    en: 'No one',
    ar: 'لا أحد',
    es: 'Nadie',
    fr: 'Personne',
    de: 'Niemand',
    pt: 'Ninguém',
    ru: 'Никто',
    hi: 'कोई नहीं',
    ja: '非表示',
    zh: '不显示'
  },
  vis_everyone_sub: {
    en: 'Anyone can tap your dot to see your name and picture and add you.',
    ar: 'يمكن لأي شخص النقر على نقطتك لرؤية اسمك وصورتك وإضافتك.',
    es: 'Cualquiera puede tocar tu punto para ver tu nombre y foto y agregarte.',
    fr: 'Tout le monde peut toucher votre point pour voir votre nom et votre photo et vous ajouter.',
    de: 'Jeder kann deinen Punkt antippen, deinen Namen und dein Bild sehen und dich hinzufügen.',
    pt: 'Qualquer pessoa pode tocar no seu ponto para ver seu nome e foto e adicionar você.',
    ru: 'Любой может нажать на вашу точку, увидеть имя и фото и добавить вас.',
    hi: 'कोई भी आपके बिंदु पर टैप करके आपका नाम और फ़ोटो देख सकता है और आपको जोड़ सकता है।',
    ja: '誰でもあなたの点をタップして名前と写真を見て、追加できます。',
    zh: '任何人都可以点按你的圆点，查看你的姓名和头像并添加你。'
  },
  vis_friends_sub: {
    en: 'Friends see your name on the map. Everyone else sees a dot.',
    ar: 'يرى أصدقاؤك اسمك على الخريطة، ويرى الآخرون نقطة فقط.',
    es: 'Tus amigos ven tu nombre en el mapa. Los demás ven un punto.',
    fr: 'Vos amis voient votre nom sur la carte. Les autres voient un point.',
    de: 'Freunde sehen deinen Namen auf der Karte. Alle anderen sehen einen Punkt.',
    pt: 'Seus amigos veem seu nome no mapa. Os outros veem um ponto.',
    ru: 'Друзья видят ваше имя на карте. Остальные — точку.',
    hi: 'मित्र मानचित्र पर आपका नाम देखते हैं। बाकी सब एक बिंदु देखते हैं।',
    ja: '友達には地図上で名前が表示され、他の人には点が表示されます。',
    zh: '好友在地图上能看到你的名字，其他人只看到一个圆点。'
  },
  vis_anon_sub: {
    en: 'You are a dot in your city. Nobody sees who you are.',
    ar: 'أنت نقطة في مدينتك، ولا أحد يعرف من أنت.',
    es: 'Eres un punto en tu ciudad. Nadie ve quién eres.',
    fr: 'Vous êtes un point dans votre ville. Personne ne voit qui vous êtes.',
    de: 'Du bist ein Punkt in deiner Stadt. Niemand sieht, wer du bist.',
    pt: 'Você é um ponto na sua cidade. Ninguém vê quem você é.',
    ru: 'Вы — точка в своём городе. Никто не видит, кто вы.',
    hi: 'आप अपने शहर में एक बिंदु हैं। कोई नहीं देखता कि आप कौन हैं।',
    ja: 'あなたは街の中の点です。誰にも正体は分かりません。',
    zh: '你是城市中的一个点，没人知道你是谁。'
  },
  vis_nobody_sub: {
    en: 'You are not on the map at all.',
    ar: 'أنت غير موجود على الخريطة إطلاقًا.',
    es: 'No apareces en el mapa.',
    fr: 'Vous n’apparaissez pas sur la carte.',
    de: 'Du bist gar nicht auf der Karte.',
    pt: 'Você não aparece no mapa.',
    ru: 'Вас нет на карте.',
    hi: 'आप मानचित्र पर बिल्कुल नहीं हैं।',
    ja: 'あなたは地図に表示されません。',
    zh: '你完全不会出现在地图上。'
  },
  page_place: {
    en: 'Put my pages on the map',
    ar: 'ضع صفحاتي على الخريطة',
    es: 'Poner mis páginas en el mapa',
    fr: 'Placer mes pages sur la carte',
    de: 'Meine Seiten auf die Karte setzen',
    pt: 'Colocar minhas páginas no mapa',
    ru: 'Добавить мои страницы на карту',
    hi: 'मेरे पेज मानचित्र पर रखें',
    ja: '自分のページを地図に表示',
    zh: '把我的主页放到地图上'
  },
  loading_world: {
    en: 'Finding everyone…',
    ar: 'جارٍ العثور على الجميع…',
    es: 'Buscando a todos…',
    fr: 'Recherche de tout le monde…',
    de: 'Alle werden gesucht …',
    pt: 'Encontrando todos…',
    ru: 'Ищем всех…',
    hi: 'सबको खोज रहे हैं…',
    ja: 'みんなを探しています…',
    zh: '正在寻找所有人…'
  },
  not_available: {
    en: 'Not available',
    ar: 'غير متاح',
    es: 'No disponible',
    fr: 'Indisponible',
    de: 'Nicht verfügbar',
    pt: 'Indisponível',
    ru: 'Недоступно',
    hi: 'उपलब्ध नहीं',
    ja: '利用できません',
    zh: '不可用'
  },
  sunrise: {
    en: 'Sunrise',
    ar: 'الشروق',
    es: 'Amanecer',
    fr: 'Lever du soleil',
    de: 'Sonnenaufgang',
    pt: 'Nascer do sol',
    ru: 'Восход',
    hi: 'सूर्योदय',
    ja: '日の出',
    zh: '日出'
  },
  sunset: {
    en: 'Sunset',
    ar: 'الغروب',
    es: 'Atardecer',
    fr: 'Coucher du soleil',
    de: 'Sonnenuntergang',
    pt: 'Pôr do sol',
    ru: 'Закат',
    hi: 'सूर्यास्त',
    ja: '日の入り',
    zh: '日落'
  },
  ph_night: {
    en: 'Night',
    ar: 'ليل',
    es: 'Noche',
    fr: 'Nuit',
    de: 'Nacht',
    pt: 'Noite',
    ru: 'Ночь',
    hi: 'रात',
    ja: '夜',
    zh: '夜晚'
  },
  ph_dawn: {
    en: 'Dawn',
    ar: 'فجر',
    es: 'Alba',
    fr: 'Aube',
    de: 'Morgendämmerung',
    pt: 'Madrugada',
    ru: 'Рассвет',
    hi: 'भोर',
    ja: '夜明け',
    zh: '黎明'
  },
  ph_sunrise: {
    en: 'Sunrise',
    ar: 'شروق',
    es: 'Amanecer',
    fr: 'Lever du soleil',
    de: 'Sonnenaufgang',
    pt: 'Nascer do sol',
    ru: 'Восход',
    hi: 'सूर्योदय',
    ja: '日の出',
    zh: '日出'
  },
  ph_day: {
    en: 'Day',
    ar: 'نهار',
    es: 'Día',
    fr: 'Jour',
    de: 'Tag',
    pt: 'Dia',
    ru: 'День',
    hi: 'दिन',
    ja: '昼',
    zh: '白天'
  },
  ph_sunset: {
    en: 'Sunset',
    ar: 'غروب',
    es: 'Atardecer',
    fr: 'Coucher du soleil',
    de: 'Sonnenuntergang',
    pt: 'Pôr do sol',
    ru: 'Закат',
    hi: 'सूर्यास्त',
    ja: '夕暮れ',
    zh: '日落'
  },
  ph_dusk: {
    en: 'Dusk',
    ar: 'شفق',
    es: 'Anochecer',
    fr: 'Crépuscule',
    de: 'Abenddämmerung',
    pt: 'Anoitecer',
    ru: 'Сумерки',
    hi: 'संध्या',
    ja: '夕闇',
    zh: '黄昏'
  },
  wx_clear: {
    en: 'Clear',
    ar: 'صافٍ',
    es: 'Despejado',
    fr: 'Dégagé',
    de: 'Klar',
    pt: 'Limpo',
    ru: 'Ясно',
    hi: 'साफ़',
    ja: '晴れ',
    zh: '晴'
  },
  wx_partly: {
    en: 'Partly cloudy',
    ar: 'غائم جزئيًا',
    es: 'Parcialmente nublado',
    fr: 'Partiellement nuageux',
    de: 'Teils bewölkt',
    pt: 'Parcialmente nublado',
    ru: 'Переменная облачность',
    hi: 'आंशिक बादल',
    ja: '晴れ時々曇り',
    zh: '多云间晴'
  },
  wx_clouds: {
    en: 'Cloudy',
    ar: 'غائم',
    es: 'Nublado',
    fr: 'Nuageux',
    de: 'Bewölkt',
    pt: 'Nublado',
    ru: 'Облачно',
    hi: 'बादल',
    ja: '曇り',
    zh: '阴'
  },
  wx_fog: {
    en: 'Fog',
    ar: 'ضباب',
    es: 'Niebla',
    fr: 'Brouillard',
    de: 'Nebel',
    pt: 'Neblina',
    ru: 'Туман',
    hi: 'कोहरा',
    ja: '霧',
    zh: '雾'
  },
  wx_drizzle: {
    en: 'Drizzle',
    ar: 'رذاذ',
    es: 'Llovizna',
    fr: 'Bruine',
    de: 'Nieselregen',
    pt: 'Garoa',
    ru: 'Морось',
    hi: 'बूंदाबांदी',
    ja: '霧雨',
    zh: '毛毛雨'
  },
  wx_rain: {
    en: 'Rain',
    ar: 'مطر',
    es: 'Lluvia',
    fr: 'Pluie',
    de: 'Regen',
    pt: 'Chuva',
    ru: 'Дождь',
    hi: 'बारिश',
    ja: '雨',
    zh: '雨'
  },
  wx_snow: {
    en: 'Snow',
    ar: 'ثلج',
    es: 'Nieve',
    fr: 'Neige',
    de: 'Schnee',
    pt: 'Neve',
    ru: 'Снег',
    hi: 'बर्फ़',
    ja: '雪',
    zh: '雪'
  },
  wx_storm: {
    en: 'Thunderstorm',
    ar: 'عاصفة رعدية',
    es: 'Tormenta',
    fr: 'Orage',
    de: 'Gewitter',
    pt: 'Tempestade',
    ru: 'Гроза',
    hi: 'आंधी-तूफ़ान',
    ja: '雷雨',
    zh: '雷暴'
  },
  kind_store: {
    en: 'Store',
    ar: 'متجر',
    es: 'Tienda',
    fr: 'Boutique',
    de: 'Geschäft',
    pt: 'Loja',
    ru: 'Магазин',
    hi: 'स्टोर',
    ja: 'ストア',
    zh: '商店'
  },
  kind_business: {
    en: 'Business',
    ar: 'نشاط تجاري',
    es: 'Negocio',
    fr: 'Entreprise',
    de: 'Unternehmen',
    pt: 'Empresa',
    ru: 'Бизнес',
    hi: 'व्यवसाय',
    ja: 'ビジネス',
    zh: '商家'
  },
  kind_restaurant: {
    en: 'Restaurant',
    ar: 'مطعم',
    es: 'Restaurante',
    fr: 'Restaurant',
    de: 'Restaurant',
    pt: 'Restaurante',
    ru: 'Ресторан',
    hi: 'रेस्टोरेंट',
    ja: 'レストラン',
    zh: '餐厅'
  },
  kind_creator: {
    en: 'Creator',
    ar: 'صانع محتوى',
    es: 'Creador',
    fr: 'Créateur',
    de: 'Creator',
    pt: 'Criador',
    ru: 'Автор',
    hi: 'क्रिएटर',
    ja: 'クリエイター',
    zh: '创作者'
  },
  kind_public_figure: {
    en: 'Public figure',
    ar: 'شخصية عامة',
    es: 'Figura pública',
    fr: 'Personnalité',
    de: 'Person des öffentlichen Lebens',
    pt: 'Figura pública',
    ru: 'Публичная личность',
    hi: 'सार्वजनिक हस्ती',
    ja: '著名人',
    zh: '公众人物'
  },
  kind_sport: {
    en: 'Sport',
    ar: 'رياضة',
    es: 'Deporte',
    fr: 'Sport',
    de: 'Sport',
    pt: 'Esporte',
    ru: 'Спорт',
    hi: 'खेल',
    ja: 'スポーツ',
    zh: '体育'
  },
  kind_education: {
    en: 'Education',
    ar: 'تعليم',
    es: 'Educación',
    fr: 'Éducation',
    de: 'Bildung',
    pt: 'Educação',
    ru: 'Образование',
    hi: 'शिक्षा',
    ja: '教育',
    zh: '教育'
  },
  kind_health: {
    en: 'Health',
    ar: 'صحة',
    es: 'Salud',
    fr: 'Santé',
    de: 'Gesundheit',
    pt: 'Saúde',
    ru: 'Здоровье',
    hi: 'स्वास्थ्य',
    ja: 'ヘルス',
    zh: '健康'
  },
  kind_tech: {
    en: 'Technology',
    ar: 'تقنية',
    es: 'Tecnología',
    fr: 'Technologie',
    de: 'Technologie',
    pt: 'Tecnologia',
    ru: 'Технологии',
    hi: 'तकनीक',
    ja: 'テクノロジー',
    zh: '科技'
  },
  kind_news: {
    en: 'News',
    ar: 'أخبار',
    es: 'Noticias',
    fr: 'Actualités',
    de: 'Nachrichten',
    pt: 'Notícias',
    ru: 'Новости',
    hi: 'समाचार',
    ja: 'ニュース',
    zh: '新闻'
  },
  kind_entertainment: {
    en: 'Entertainment',
    ar: 'ترفيه',
    es: 'Entretenimiento',
    fr: 'Divertissement',
    de: 'Unterhaltung',
    pt: 'Entretenimento',
    ru: 'Развлечения',
    hi: 'मनोरंजन',
    ja: 'エンタメ',
    zh: '娱乐'
  },
  kind_community: {
    en: 'Community',
    ar: 'مجتمع',
    es: 'Comunidad',
    fr: 'Communauté',
    de: 'Community',
    pt: 'Comunidade',
    ru: 'Сообщество',
    hi: 'समुदाय',
    ja: 'コミュニティ',
    zh: '社区'
  },
  kind_page: {
    en: 'Page',
    ar: 'صفحة',
    es: 'Página',
    fr: 'Page',
    de: 'Seite',
    pt: 'Página',
    ru: 'Страница',
    hi: 'पेज',
    ja: 'ページ',
    zh: '主页'
  },
  view_as: {
    en: 'View the map as',
    ar: 'عرض الخريطة بصفة',
    es: 'Ver el mapa como',
    fr: 'Voir la carte en tant que',
    de: 'Karte ansehen als',
    pt: 'Ver o mapa como',
    ru: 'Смотреть карту как',
    hi: 'मानचित्र इस रूप में देखें',
    ja: 'マップの表示',
    zh: '以此身份查看地图'
  },
  view_as_me: {
    en: 'Me',
    ar: 'أنا',
    es: 'Yo',
    fr: 'Moi',
    de: 'Ich',
    pt: 'Eu',
    ru: 'Я',
    hi: 'मैं',
    ja: '自分',
    zh: '我自己'
  },
  appear_as_page: {
    en: 'Appear as {page}',
    ar: 'الظهور بصفة {page}',
    es: 'Aparecer como {page}',
    fr: 'Apparaître en tant que {page}',
    de: 'Als {page} erscheinen',
    pt: 'Aparecer como {page}',
    ru: 'Показываться как {page}',
    hi: '{page} के रूप में दिखें',
    ja: '{page}として表示',
    zh: '以 {page} 的身份出现'
  },
  appear_as_page_sub: {
    en: 'People see your page at its address, not your face. You still count in your city, nameless.',
    ar: 'يرى الناس صفحتك في عنوانها بدلاً من صورتك، وتبقى محسوباً في مدينتك دون اسم.',
    es: 'La gente ve tu página en su dirección, no tu cara. Sigues contando en tu ciudad, sin nombre.',
    fr: 'On voit votre page à son adresse, pas votre visage. Vous comptez toujours dans votre ville, sans nom.',
    de: 'Andere sehen deine Seite an ihrer Adresse, nicht dein Gesicht. In deiner Stadt zählst du weiter, ohne Namen.',
    pt: 'As pessoas veem sua página no endereço dela, não seu rosto. Você continua contando na sua cidade, sem nome.',
    ru: 'Люди видят вашу страницу по её адресу, а не вас. Вы по-прежнему учитываетесь в своём городе, без имени.',
    hi: 'लोग आपका चेहरा नहीं, आपके पेज को उसके पते पर देखते हैं। आप अपने शहर में बिना नाम के गिने जाते रहेंगे।',
    ja: '顔ではなく、ページがその住所に表示されます。街の人数には名前なしで含まれます。',
    zh: '大家会在主页地址看到你的主页，而不是你本人。你仍以匿名方式计入所在城市。'
  },
  page_view_title: {
    en: '{page} on the map',
    ar: '{page} على الخريطة',
    es: '{page} en el mapa',
    fr: '{page} sur la carte',
    de: '{page} auf der Karte',
    pt: '{page} no mapa',
    ru: '{page} на карте',
    hi: 'मानचित्र पर {page}',
    ja: 'マップ上の{page}',
    zh: '{page} 在地图上'
  },
  page_followers_n: {
    en: '{n} followers on the map',
    ar: '{n} متابع على الخريطة',
    es: '{n} seguidores en el mapa',
    fr: '{n} abonnés sur la carte',
    de: '{n} Follower auf der Karte',
    pt: '{n} seguidores no mapa',
    ru: '{n} подписчиков на карте',
    hi: 'मानचित्र पर {n} फ़ॉलोअर',
    ja: 'マップ上のフォロワー{n}人',
    zh: '地图上有 {n} 位关注者'
  },
  page_same_kind_n: {
    en: '{n} pages like yours',
    ar: '{n} صفحة مثل صفحتك',
    es: '{n} páginas como la tuya',
    fr: '{n} pages comme la vôtre',
    de: '{n} Seiten wie deine',
    pt: '{n} páginas como a sua',
    ru: '{n} похожих страниц',
    hi: 'आपके जैसे {n} पेज',
    ja: '同じ種類のページ{n}件',
    zh: '{n} 个同类主页'
  },
  page_not_placed: {
    en: 'Add an address to this page to put it on the map',
    ar: 'أضف عنواناً لهذه الصفحة لتظهر على الخريطة',
    es: 'Añade una dirección a esta página para ponerla en el mapa',
    fr: 'Ajoutez une adresse à cette page pour la placer sur la carte',
    de: 'Gib der Seite eine Adresse, um sie auf die Karte zu bringen',
    pt: 'Adicione um endereço a esta página para colocá-la no mapa',
    ru: 'Добавьте адрес странице, чтобы она появилась на карте',
    hi: 'इस पेज को मानचित्र पर लाने के लिए उसका पता जोड़ें',
    ja: 'マップに載せるにはページに住所を追加してください',
    zh: '为该主页添加地址即可显示在地图上'
  },
  back_to_me: {
    en: 'Back to my map',
    ar: 'العودة إلى خريطتي',
    es: 'Volver a mi mapa',
    fr: 'Revenir à ma carte',
    de: 'Zurück zu meiner Karte',
    pt: 'Voltar ao meu mapa',
    ru: 'Вернуться к моей карте',
    hi: 'मेरे मानचित्र पर वापस',
    ja: '自分のマップに戻る',
    zh: '返回我的地图'
  },
  err_hidden: {
    en: "You're set to No one — change who sees you on the map first",
    ar: 'إعدادك «لا أحد» — غيّر من يراك على الخريطة أولاً',
    es: 'Estás en «Nadie»: primero cambia quién te ve en el mapa',
    fr: 'Vous êtes réglé sur « Personne » — changez d’abord qui vous voit sur la carte',
    de: 'Du bist auf „Niemand“ gestellt – ändere zuerst, wer dich auf der Karte sieht',
    pt: 'Você está em "Ninguém" — primeiro mude quem vê você no mapa',
    ru: 'У вас стоит «Никто» — сначала измените, кто видит вас на карте',
    hi: 'आपकी सेटिंग "कोई नहीं" है — पहले बदलें कि मैप पर आपको कौन देखे',
    ja: '「誰にも表示しない」になっています。まずマップで表示する相手を変更してください',
    zh: '你设置了“不显示”——请先更改地图上谁能看到你'
  },
  err_unknown_city: {
    en: "That city could not be found on the map — search for it instead",
    ar: 'تعذّر العثور على هذه المدينة على الخريطة — ابحث عنها بدلاً من ذلك',
    es: 'No se encontró esa ciudad en el mapa; búscala',
    fr: 'Impossible de trouver cette ville sur la carte — recherchez-la',
    de: 'Die Stadt wurde auf der Karte nicht gefunden – such sie stattdessen',
    pt: 'Não foi possível achar essa cidade no mapa — pesquise por ela',
    ru: 'Город не найден на карте — найдите его через поиск',
    hi: 'यह शहर मानचित्र पर नहीं मिला — इसे खोजें',
    ja: 'その都市が見つかりませんでした。検索してください',
    zh: '地图上找不到该城市，请搜索'
  },
  err_busy: {
    en: 'The map is busy — try again in a moment',
    ar: 'الخريطة مشغولة — حاول بعد قليل',
    es: 'El mapa está ocupado; inténtalo en un momento',
    fr: 'La carte est occupée — réessayez dans un instant',
    de: 'Die Karte ist ausgelastet – gleich noch einmal',
    pt: 'O mapa está ocupado — tente daqui a pouco',
    ru: 'Карта занята — попробуйте чуть позже',
    hi: 'मानचित्र व्यस्त है — थोड़ी देर में फिर कोशिश करें',
    ja: 'マップが混み合っています。少し後でお試しください',
    zh: '地图繁忙，请稍后再试'
  },
  err_slow: {
    en: 'Too many changes — try again in a few minutes',
    ar: 'تغييرات كثيرة — حاول بعد بضع دقائق',
    es: 'Demasiados cambios; inténtalo en unos minutos',
    fr: 'Trop de changements — réessayez dans quelques minutes',
    de: 'Zu viele Änderungen – in ein paar Minuten noch einmal',
    pt: 'Muitas alterações — tente em alguns minutos',
    ru: 'Слишком много изменений — попробуйте через несколько минут',
    hi: 'बहुत सारे बदलाव — कुछ मिनट बाद फिर कोशिश करें',
    ja: '変更が多すぎます。数分後にお試しください',
    zh: '更改过于频繁，请几分钟后再试'
  },
  not_saved: {
    en: "That did not save — try again",
    ar: 'لم يُحفظ ذلك — حاول مرة أخرى',
    es: 'No se guardó; inténtalo de nuevo',
    fr: "Ce n'a pas été enregistré — réessayez",
    de: 'Das wurde nicht gespeichert – versuch es noch einmal',
    pt: 'Não foi salvo — tente de novo',
    ru: 'Не сохранилось — попробуйте ещё раз',
    hi: 'यह सहेजा नहीं गया — फिर कोशिश करें',
    ja: '保存できませんでした。もう一度お試しください',
    zh: '未能保存，请重试'
  }
};

/** What to tell somebody when the server said no: the reason, not a shrug. */
export function mapErrorKey(r) {
  const e = r && (r.error || '');
  if (e === 'unknown_city' || e === 'no_city' || e === 'bad_place') return 'err_unknown_city';
  if (e === 'busy' || e === 'unavailable' || r && r.status === 503) return 'err_busy';
  if (e === 'slow_down' || r && r.status === 429) return 'err_slow';
  return 'not_available';
}
export function mapL(key, lang, vars) {
  const row = W[key];
  const l = String(lang || 'en').slice(0, 2);
  const v = row ? row[l] || row.en : key;
  return vars ? v.replace(/\{(\w+)\}/g, (_m, k) => String(vars[k] ?? '')) : v;
}

/** 12400 → "12.4K", in the reader's digits. */
export function compactCount(n, lang) {
  const v = Math.max(0, Math.floor(Number(n) || 0));
  try {
    return new Intl.NumberFormat(String(lang || 'en').slice(0, 2), {
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(v);
  } catch {
    if (v >= 1e6) return `${(v / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1).replace(/\.0$/, '')}K`;
    return String(v);
  }
}
